import { useEffect, useRef } from "react";
import { usePrimaryComplaintStore } from "@/store/primaryComplaint";
import {
  complaintName,
  complaintOrdinal,
  complaintStepPosition,
  resolveComplaintType,
  type ComplaintStepKey,
} from "@/utils/complaintSteps";
import { Button } from "@/components/ui/Button";

interface ComplaintStepHeaderProps {
  complaintId: string | null;
  step: ComplaintStepKey;
  /** Overrides the complaint-based heading (e.g. "What makes it better or worse?"). */
  heading?: string;
}

/**
 * Progress + heading for every screen of one complaint: "Lower back pain · Complaint 2 · Step 3 of 5"
 * over a bar, then "Your lower back pain". Counts come from the complaint's real path, so they never
 * jump (numbness skips radiation) or restart (outcome assessment is one of the steps).
 */
export function ComplaintStepHeader({ complaintId, step, heading }: ComplaintStepHeaderProps) {
  const complaintHeaders = usePrimaryComplaintStore((s) => s.complaintHeaders);
  const visitReason = usePrimaryComplaintStore((s) => s.visitReason);
  const region = usePrimaryComplaintStore((s) => s.humanBody?.selectedPart?.label ?? "");
  const fetchComplaints = usePrimaryComplaintStore((s) => s.fetchComplaints);

  const header = complaintHeaders?.find((h) => String(h.id) === String(complaintId));

  // The list supplies the real type and "Complaint N". It's missing after a refresh, and stale right
  // after a new complaint is created, so fetch it once per complaint when this one isn't in it.
  const fetchedFor = useRef<string | null>(null);
  useEffect(() => {
    if (complaintId == null || header || fetchedFor.current === complaintId) return;
    fetchedFor.current = complaintId;
    void fetchComplaints().catch(() => {});
  }, [complaintId, header, fetchComplaints]);
  const type = resolveComplaintType(complaintHeaders, complaintId, visitReason);
  // The stored problem name ("Numbness in the Left Wrist") is what the complaint list shows and what
  // PryzmWeb labels it with (PortalProblemName falls through to Name), so patients and clinicians see
  // the same words. Region + type is only a fallback until the list has loaded.
  const storedName = header?.description?.trim() ?? "";
  const derivedName = complaintName(header?.region || region, type);
  const name = storedName || derivedName;
  const position = complaintStepPosition(type, step);
  const ordinal = complaintOrdinal(complaintHeaders, complaintId);

  const meta = [
    name,
    ordinal != null && (complaintHeaders?.length ?? 0) > 1 ? `Complaint ${ordinal}` : null,
    position ? `Step ${position.index} of ${position.total}` : null,
  ].filter(Boolean);

  return (
    <div className="complaint-step-header">
      <p className="complaint-step-header__meta">{meta.join(" · ")}</p>
      <div
        className="complaint-history-progress-track"
        role="progressbar"
        aria-label="Progress for this complaint"
        aria-valuemin={0}
        aria-valuemax={position?.total ?? 0}
        aria-valuenow={position?.index ?? 0}
      >
        <div
          className="complaint-history-progress-fill"
          style={{ width: position ? `${(position.index / position.total) * 100}%` : "0%" }}
        />
      </div>
      <h2 className="complaint-step-header__heading">
        {heading ?? (storedName || (derivedName ? `Your ${derivedName.toLowerCase()}` : "Your complaint"))}
      </h2>
    </div>
  );
}

/** Inline failure with retry. Saved answers are untouched, and the patient is told so. */
export function StepLoadError({
  onRetry,
  message = "We couldn't load your answers. Your saved answers are safe.",
}: {
  onRetry: () => void;
  message?: string;
}) {
  return (
    <div className="step-load-error" role="alert">
      <p>{message}</p>
      <Button type="button" variant="outline" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}
